import { createPortal } from 'react-dom';
import { useEffect, useMemo, type ComponentType } from 'react';
import type { GridItemHTMLElement } from 'gridstack';
import { useGridStackContext } from './grid-stack-context';
import { useGridStackRenderContext } from './grid-stack-render-context';
import { GridStackWidgetContext } from './grid-stack-widget-context';
import { GridStackWidget } from 'gridstack';
import WidgetBase from '../Widgets/WidgetBase';
import { CurveType } from '@mui/x-charts';
import { WidgetOptions } from '../dashboardTypes';
import { parseDashboardWidgetContent } from '../../../utils/sensor-stream-tracker';

export interface ComponentDataType<T = object> {
  name: string;
  curve: string;
  style: string;
  dataKey: string;
  sensorKeys: string[];
  options?: WidgetOptions;
  props: T;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ComponentMap = Record<string, ComponentType<any>>;

/** Parses GridStack widget JSON through {@link parseDashboardWidgetContent} (resolves `motor_multi` stream keys). */
export function parseWeightMetaToComponentData(meta: GridStackWidget): ComponentDataType & { error: unknown } {
  let error = null;
  const defaults: ComponentDataType = {
    name: '',
    curve: 'linear',
    style: 'line',
    dataKey: 'all',
    sensorKeys: [],
    props: {},
  };

  try {
    const parsed = parseDashboardWidgetContent(meta.content);
    if (parsed) {
      return {
        name: parsed.name,
        curve: parsed.curve,
        style: parsed.style,
        dataKey: parsed.dataKey,
        sensorKeys: parsed.sensorKeys,
        options: parsed.options,
        props: parsed.props,
        error,
      };
    }
  } catch (e) {
    error = e;
  }

  return { ...defaults, error };
}

function GridStackWidgetPortal({ id, meta }: { id: string; meta: GridStackWidget }) {
  const { gridStack } = useGridStackContext();
  const { getWidgetContainer } = useGridStackRenderContext();
  const componentData = useMemo(
    () => parseWeightMetaToComponentData(meta),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- meta.content is the stable layout payload key
    [id, meta.content]
  );
  const widgetContainer = getWidgetContainer(id);

  // GridStack binds drag handles when widgets are created, before React portals mount `.widget-header`.
  // Re-init drag/resize after portal content exists so edges resize and only the header moves.
  useEffect(() => {
    if (!gridStack || !widgetContainer) return;
    const gridItem = widgetContainer.closest('.grid-stack-item') as GridItemHTMLElement | null;
    if (!gridItem) return;
    gridStack.prepareDragDrop(gridItem, true);
  }, [gridStack, id, widgetContainer]);

  if (!widgetContainer) {
    throw new Error(`Widget container not found for id: ${id}`);
  }

  return (
    <GridStackWidgetContext.Provider value={{ widget: { id } }}>
      {createPortal(
        <WidgetBase
          type={componentData.name}
          curve={componentData.curve as CurveType}
          id={id}
          style={componentData.style}
          dataKey={componentData.dataKey}
          sensorKeys={componentData.sensorKeys}
          options={componentData.options}
        />,
        widgetContainer
      )}
    </GridStackWidgetContext.Provider>
  );
}

export function GridStackRender() {
  const { _rawWidgetMetaMap } = useGridStackContext();

  return (
    <>
      {Array.from(_rawWidgetMetaMap.value.entries()).map(([id, meta]) => (
        <GridStackWidgetPortal key={id} id={id} meta={meta} />
      ))}
    </>
  );
}
